import { ref, onMounted, onUnmounted } from "vue";
import { checkSessionStatus, updateSessionActivity } from "./useSessionManager";
import { useAuthStore } from "@/stores/auth";
import { useRouter } from "vue-router";

export function useSessionMonitor() {
  const authStore = useAuthStore();
  const router = useRouter();
  const isSessionValid = ref(true);
  let sessionCheckInterval = null;
  let activityUpdateInterval = null;

  const startSessionMonitoring = () => {
    const sessionId = localStorage.getItem("sessionId");

    if (!sessionId || !authStore.isAuthenticated) {
      return;
    }

    // Check session status every 30 seconds
    sessionCheckInterval = setInterval(async () => {
      try {
        const result = await checkSessionStatus(sessionId);

        if (result.success && !result.isActive) {
          // Session has been revoked
          isSessionValid.value = false;
          await handleSessionRevoked();
        }
      } catch (error) {
        console.error("Error checking session status:", error);
      }
    }, 30000); // 30 seconds

    // Update session activity every 5 minutes
    activityUpdateInterval = setInterval(async () => {
      try {
        await updateSessionActivity(sessionId);
      } catch (error) {
        console.error("Error updating session activity:", error);
      }
    }, 5 * 60 * 1000); // 5 minutes
  };

  const handleSessionRevoked = async () => {
    // Clear local storage
    localStorage.removeItem("sessionId");
    localStorage.removeItem("user");
    localStorage.removeItem("userData");

    // Sign out from auth store
    authStore.clearUser();

    // Show notification
    alert(
      "Your session has been terminated by an administrator. You will be redirected to the login page."
    );

    // Redirect to login
    router.push("/borenn");
  };

  const stopSessionMonitoring = () => {
    if (sessionCheckInterval) {
      clearInterval(sessionCheckInterval);
      sessionCheckInterval = null;
    }

    if (activityUpdateInterval) {
      clearInterval(activityUpdateInterval);
      activityUpdateInterval = null;
    }
  };

  // Update activity on user interactions
  const updateActivityOnInteraction = () => {
    const sessionId = localStorage.getItem("sessionId");
    if (sessionId) {
      updateSessionActivity(sessionId).catch((error) => {
        console.error("Error updating activity on interaction:", error);
      });
    }
  };

  // Listen for user activity
  const setupActivityListeners = () => {
    const events = ["click", "keypress", "scroll", "mousemove"];
    let lastActivity = Date.now();

    const throttledUpdate = () => {
      const now = Date.now();
      // Only update if it's been more than 1 minute since last update
      if (now - lastActivity > 60000) {
        lastActivity = now;
        updateActivityOnInteraction();
      }
    };

    events.forEach((event) => {
      document.addEventListener(event, throttledUpdate, { passive: true });
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, throttledUpdate);
      });
    };
  };

  onMounted(() => {
    startSessionMonitoring();
    const cleanupListeners = setupActivityListeners();

    onUnmounted(() => {
      stopSessionMonitoring();
      cleanupListeners();
    });
  });

  return {
    isSessionValid,
    startSessionMonitoring,
    stopSessionMonitoring,
    updateActivityOnInteraction,
  };
}
